import { FC, HTMLAttributes, ReactNode } from 'react';
import clx from 'classnames';
import FileIcon from '../FileIcon/FileIcon';
import { Hint } from '@redoc/ui-kit';
import { safeDecodeURIComponent } from 'src/Utils/TextUtils';
import { EditableField } from '../Controls';
import DateFormater from 'src/Utils/DateFormater';
import { ReactComponent as LinkIcon } from 'src/Themes/assets/chevron-left.svg';
import st from './FileInfo.module.scss';

interface IFileInfoProps extends HTMLAttributes<HTMLDivElement> {
	name?: string;
	size?: number;
	lastUpdated?: string;
	isActive?: boolean;
	innerToolbar?: boolean;
	notHovered?: boolean;
	isLink?: boolean;
	isCurrentUserSignRequested?: boolean;
	renderToolbar?: (props: IFileInfoProps) => ReactNode;
	onChangeTitle?: (value: string) => void;
}

const FileInfo: FC<IFileInfoProps> = (props) => {
	const {
		id,
		title,
		size = 0,
		name = title,
		isActive = false,
		innerToolbar = false,
		notHovered = false,
		isLink = false,
		isCurrentUserSignRequested = false,
		lastUpdated,
		renderToolbar,
		onChangeTitle,
		className,
		...otherProps
	} = props;

	const renderTitle = () => {
		const prepareTitle = safeDecodeURIComponent(title ?? '');

		const Title = (
			<h6 className="body-large" title={prepareTitle}>
				{prepareTitle}
			</h6>
		);

		if (onChangeTitle) {
			return (
				<EditableField
					initValue={prepareTitle}
					onChangeValue={onChangeTitle}
					renderView={() => {
						return Title;
					}}
					min
				/>
			);
		}

		return Title;
	};

	const classes = clx(st.fileInfo, {
		[st.innerToolbar]: innerToolbar,
		[st.active]: isActive,
		[st.notHovered]: notHovered,
		[st.currentUserRequest]: isCurrentUserSignRequested,
	});

	return (
		<div className={classes} id={`file-${id}`} {...otherProps}>
			<FileIcon fileName={name} />
			<div className={st.textBlock}>
				{renderTitle()}

				<div className={st.bottom}>
					{size !== undefined && size > 0 && (
						<span className="body-small">{getFileSize(size)}</span>
					)}

					{lastUpdated && (
						<span className="body-small">
							{DateFormater.toDateWithTimeString(lastUpdated)}
						</span>
					)}

					{title !== name && (
						<span className="body-small">
							{safeDecodeURIComponent(name ?? '')}
						</span>
					)}
				</div>
			</div>

			<div className={st.toolbar}>
				{isLink && <LinkIcon className={st.toolbarLink} />}
				{!!renderToolbar && renderToolbar(props)}
			</div>
		</div>
	);
};

const getFileSize = (size: number) => {
	const kb = size / 1000;
	if (kb > 1000) {
		return (kb / 1000).toFixed(2) + ' Мб';
	}
	return kb.toFixed(2) + ' Кб';
};

export default FileInfo;
